<template>
	<div class="c-phone-item">
		<div class="input-container" :class="{ 'input-focus': focus, 'input-error': !!errorMessage }">
			<div class="select-block">
				<el-select
					:value="item.areaCode"
					:class="{ 'active-area-code': item.phone }"
					filterable
					:filter-method="val => getDataByInput(val)"
					popper-class="c-phone-item--selector"
					:popper-append-to-body="true"
					:disabled="disabled"
					@blur="triggerValidate"
					@focus="focus = true"
					@change="changeAreaCode"
					@visible-change="initAreaCodeArr"
				>
					<el-option-group
						v-for="list in searchAreaCodeArr"
						:key="list.key"
						:label="list.key == '#' ? '' : list.key"
					>
						<el-option
							v-for="(countryArea, index) in list.countryAreaList"
							:key="index"
							:value="countryArea.areacode"
							:label="countryArea.areacode"
						>
							<p class="areaCode-option">
								<span class="areaCode-value">{{ countryArea.areacode }}</span>
								<span class="areaCode-content">{{ countryArea.countryname }}</span>
							</p>
						</el-option>
					</el-option-group>
				</el-select>
			</div>
			<el-input
				:value="item.phone"
				class="phone-input"
				:maxlength="maxlength"
				:minlength="minlength"
				:disabled="disabled"
				@input="formatInput"
				@blur="triggerValidate"
				@focus="focus = true"
				@paste.native.prevent="phonePaste"
				:placeholder="getPhonePlaceholder"
			></el-input>
			<div>
				<slot></slot>
			</div>
		</div>
		<div class="el-form-item__error error-validate">
			{{ errorMessage }}
		</div>
	</div>
</template>

<script>
import formMixin from './mixins';
import { debounce, deepClone, checkAreaCode, noop } from '@/utils/api';
import { convertToServiceFormat } from './local-area';

export default {
	props: {
		item: Object,
		required: {
			type: Boolean,
			default: false
		},
		blurValidate: {
			type: Boolean,
			default: true
		},
		placeholder: {
			type: String,
			default: ''
		},
		disabled: {
			type: Boolean,
			default: false
		},
		maxlength: Number,
		minlength: Number
	},
	inject: {
		elForm: {
			default: ''
		},
		elFormItem: {
			default: ''
		}
	},
	mixins: [formMixin],
	data() {
		return {
			originItem: {},
			nationAreaCodeArr: [],
			searchAreaCodeArr: [],
			errorMessage: '',
			validateRules: [
				{
					name: 'areaCode',
					validate: () => {
						if (!checkAreaCode(this.item.areaCode)) {
							this.errorMessage = this.$t('enterprise.normal-field.invalid-area-code');
							return false;
						}
						return true;
					}
				},
				{
					name: 'phone',
					validate: () => {
						if (!this.item.phone) {
							this.errorMessage = this.$t('enterprise.normal-field.enter-phone-number');
							return false;
						}
						return true;
					}
				}
			],
			focus: false,
			selecting: false
		};
	},
	computed: {
		isPhoneParent() {
			return this.$parent.$options.name === 'CPhone';
		},
		getPhonePlaceholder() {
			return this.placeholder || this.$t('enterprise.normal-field.enter-phone-number');
		}
	},
	watch: {
		required: {
			handler: function (newVal) {
				if (!newVal) {
					this.clearValidate();
				}
			},
			immediate: true
		},
		item: {
			handler: function () {
				this.setDefaultFill();
			},
			immediate: true
		}
	},
	mounted() {
		this.originItem = deepClone(this.item);

		if (!this.isPhoneParent) {
			this.resetFormMethods();
		}

		const localAreaCode = convertToServiceFormat();
		this.nationAreaCodeArr = localAreaCode;
		this.searchAreaCodeArr = localAreaCode;
	},
	methods: {
		setDefaultFill() {
			if (!this.item.areaCode) {
				this.item.areaCode = '+86';
			}
			if (!this.item.phone) {
				this.item.phone = '';
			}
		},
		initAreaCodeArr(visible) {
			if (visible && this.searchAreaCodeArr.length == 0) {
				this.searchAreaCodeArr = this.nationAreaCodeArr;
			}
		},
		phonePaste(event) {
			const clipboardData = event.clipboardData;
			const pastePhone = clipboardData.getData('text');
			const regex = /\D+/gm;
			this.item.phone = pastePhone.replace(regex, '');

			this.$forceUpdate();
		},
		getDataByInput: debounce(function (val) {
			this.item.areaCode = val;
			if (val) {
				this.searchAreaCodeArr = deepClone(this.nationAreaCodeArr)
					.map(ele => {
						ele.countryAreaList = ele.countryAreaList.filter(item => {
							if (item.areacode.includes(val) || item.countryname.includes(val)) {
								return true;
							}
						});
						return ele;
					})
					.filter(ele => ele.countryAreaList.length > 0);
			} else {
				this.searchAreaCodeArr = this.nationAreaCodeArr;
			}
			// 不加不会响应式
			this.$forceUpdate();
		}),
		changeAreaCode(val) {
			this.item.areaCode = val;
			this.$forceUpdate();
			this.$emit('change', this.item);
		},
		formatInput(value) {
			if (value === '' || /^\d+$/.test(value)) {
				this.item.phone = value;
				this.$forceUpdate();
			}
		},
		triggerValidate() {
			this.$emit('blur');
			this.focus = false;
			if (!this.blurValidate) {
				return;
			}

			setTimeout(() => {
				// 必须延时，才能校验改变后的areaCode
				this.validate();
			}, 100);
		},
		validate(callback = noop) {
			if (!this.required && !this.item.phone) {
				callback();
				return true;
			}

			let result = true;
			this.errorMessage = '';
			for (let rule of this.validateRules) {
				if (!rule.validate()) {
					result = false;
					break;
				}
			}

			callback(this.errorMessage, {
				[this.elFormItem.prop]: [
					{
						message: this.errorMessage,
						field: this.elFormItem.prop
					}
				]
			});

			if (!this.isPhoneParent) {
				this.elForm && this.elForm.$emit('validate', this.elFormItem.prop, result, this.errorMessage || null);
			}
		},
		resetField() {
			this.item.areaCode = this.originItem.areaCode;
			this.item.phone = this.originItem.phone;
			this.$forceUpdate();
		},
		clearValidate() {
			this.errorMessage = '';
		}
	}
};
</script>

<style lang="less">
.c-phone-item--selector {
	--theme-color: #3964e5; // 覆盖主题色
	position: static;
	min-width: 350px !important;

	&.active-area-code {
		.el-input__inner {
			color: var(--emphasis-color);
		}
	}

	&.el-select-dropdown {
		padding: 4px 6px;
	}

	.el-input__inner {
		color: var(--disabled-color);
	}

	.el-input__suffix {
		right: 4px;
	}
	.el-select__caret {
		display: flex;
		align-items: center;
		justify-content: center;
		transform: scale(0.5) rotateZ(90deg) !important;

		&::before {
			content: '\e644';
			font-family: 'iconfont' !important;
			font-size: 16px;

			-webkit-font-smoothing: antialiased;
			-moz-osx-font-smoothing: grayscale;
			color: var(--disabled-color);
		}

		&.is-reverse {
			transform: scale(0.5) rotateZ(270deg) !important;

			&::before {
				color: var(--theme-color);
			}
		}
	}

	.el-select-dropdown__wrap {
		max-height: 240px;
	}

	&& .el-select-group__wrap {
		padding-bottom: 0;

		&::after {
			content: none;
		}
	}

	.el-select-group__title {
		padding-left: 10px;

		font-weight: 500;
		font-size: 14px;
		line-height: 32px;
		color: var(--disabled-color);
	}

	.el-select-dropdown__item {
		height: 32px;
		padding: 0 10px;
		border-radius: 2px;
		display: flex;
		align-items: center;

		span {
			width: unset;
			border: 0;
			padding: 0;
		}

		&.hover {
			color: unset;
		}
		&.selected {
			.areaCode-option {
				color: var(--theme-color);
			}
		}
	}

	.areaCode-option {
		display: flex;
		align-items: center;

		font-weight: 400;
		font-size: 14px;
		color: var(--emphasis-color);

		&:hover {
			color: var(--theme-color);
		}

		.areaCode-value {
			width: 50px;
		}
	}
}
</style>
<style lang="less" scoped>
.c-phone-item {
	position: relative;

	.input-container {
		position: relative;
		width: 100%;
		height: 100%;
		min-height: 32px;
		display: flex;
		align-items: stretch;

		border: 1px solid #e5e6eb;
		border-radius: 2px;

		transition: all 0.2s;

		&.input-focus {
			border-color: var(--theme-color);
		}
		&.input-error {
			border-color: var(--error-color);
		}
	}

	/deep/ .el-input {
		.el-input__inner {
			height: 30px;
			border: unset;

			font-weight: 400;
			font-size: 14px;
			line-height: 30px;
			color: var(--emphasis-color);
		}
	}

	.select-block {
		flex-shrink: 0;
		position: static;
		min-width: 70px;
		max-width: 90px;
	}

	/deep/ .phone-input {
		flex: 1;

		&::before {
			content: '';
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			left: 0;
			width: 1px;
			height: 16px;
			background: var(--border-dark-color);
		}
	}
}
</style>
